import { useState, useEffect } from 'react';
import { apiFetch } from './shared';

const API = '/api';

function localDate(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const STATUS_COLOR = {
  hoch:    '#4a9c6f',
  normal:  '#888',
  niedrig: '#cf7f50',
  gut:     '#4a9c6f',
  mittel:  '#c17f24',
  schlecht:'#cf4f4f',
};

export default function RadSession({ onClose }) {
  const [empf, setEmpf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manuell, setManuell] = useState(false);
  const [einst, setEinst] = useState(null);
  const [einstellungOpen, setEinstellungOpen] = useState(false);
  const [einstellungForm, setEinstellungForm] = useState({ max_hr: '', z2_hr_low: '', z2_hr_high: '' });
  const [einstellungStatus, setEinstellungStatus] = useState('idle');

  // AI + Einstellungen beim Öffnen laden
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    laden();
    apiFetch(API + '/einstellungen')
      .then(r => r.json())
      .then(d => {
        setEinst(d);
        setEinstellungForm({ max_hr: d.max_hr, z2_hr_low: d.z2_hr_low, z2_hr_high: d.z2_hr_high });
      })
      .catch(() => {});
  }, []);
  const [form, setForm] = useState({ datum: localDate(), dauer_min: '', avg_power: '', avg_hr: '', rpe: '', notiz: '' });
  const [saveStatus, setSaveStatus] = useState('idle');

  function laden() {
    setLoading(true);
    apiFetch(API + '/rad/ai-empfehlung')
      .then(r => r.json())
      .then(d => { setEmpf(d.error ? { empfehlung: 'KI nicht verfügbar (' + d.error.slice(0, 60) + ')' } : d); setLoading(false); })
      .catch(() => { setEmpf({ empfehlung: 'Verbindungsfehler.' }); setLoading(false); });
  }

  function formChange(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  function saveEinstellungen() {
    setEinstellungStatus('saving');
    apiFetch(API + '/einstellungen', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        max_hr: parseInt(einstellungForm.max_hr) || undefined,
        z2_hr_low: parseInt(einstellungForm.z2_hr_low) || undefined,
        z2_hr_high: parseInt(einstellungForm.z2_hr_high) || undefined,
      }),
    })
      .then(r => r.json())
      .then(() => {
        setEinstellungStatus('saved');
        apiFetch(API + '/einstellungen').then(r => r.json()).then(d => { setEinst(d); });
        laden();
        setTimeout(() => { setEinstellungStatus('idle'); setEinstellungOpen(false); }, 1500);
      })
      .catch(() => { setEinstellungStatus('error'); setTimeout(() => setEinstellungStatus('idle'), 2000); });
  }

  function saveManual() {
    const { datum, dauer_min, avg_power, avg_hr, rpe, notiz } = form;
    if (!dauer_min) return;
    setSaveStatus('saving');
    apiFetch(API + '/einheiten', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        datum,
        typ: 'zone2',
        geplant: 0,
        dauer_min: parseInt(dauer_min) || null,
        notiz: [
          avg_power ? 'Ø' + avg_power + 'W' : '',
          avg_hr    ? 'Ø' + avg_hr + 'bpm' : '',
          rpe       ? 'RPE ' + rpe : '',
          notiz,
        ].filter(Boolean).join(' · ') || null,
      }),
    })
      .then(r => r.json())
      .then(() => { setSaveStatus('saved'); setTimeout(() => setSaveStatus('idle'), 3000); })
      .catch(() => { setSaveStatus('error'); setTimeout(() => setSaveStatus('idle'), 2000); });
  }

  const hrv_col   = empf ? (STATUS_COLOR[empf.hrv_status]   || '#888') : '#888';
  const schlaf_col = empf ? (STATUS_COLOR[empf.schlaf_status] || '#888') : '#888';

  return (
    <div style={{display:'flex', flexDirection:'column', gap:12}}>

      {/* Header */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <div style={{fontSize:16, fontWeight:700, color:'#2a6fa8'}}>Rad</div>
        <button onClick={onClose} style={{background:'transparent', border:'1px solid #2a2a2a', borderRadius:6, color:'#666', fontSize:12, padding:'4px 12px', cursor:'pointer', fontFamily:'inherit'}}>
          Zurück
        </button>
      </div>

      {/* Signal-Chips — nur wenn geladen */}
      {empf && (
        <div style={{background:'#141414', borderRadius:8, padding:'12px 16px', display:'flex', gap:16, flexWrap:'wrap'}}>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:12, color: hrv_col, fontWeight:700, textTransform:'uppercase'}}>{empf.hrv_status}</div>
            <div style={{fontSize:10, color:'#444'}}>HRV</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:12, color: schlaf_col, fontWeight:700, textTransform:'uppercase'}}>{empf.schlaf_status}</div>
            <div style={{fontSize:10, color:'#444'}}>Schlaf heute</div>
          </div>
          {empf.naechte7d > 0 && (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:12, fontWeight:700, color: empf.schlaf_trend === 'schlecht' ? '#cf4f4f' : empf.schlaf_trend === 'mittel' ? '#c17f24' : '#4a9c6f', textTransform:'uppercase'}}>
                {empf.schlechtNaechte7d}/{empf.naechte7d}
              </div>
              <div style={{fontSize:10, color:'#444'}}>Schlaf 7d</div>
            </div>
          )}
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:14, fontWeight:700, color:'#5a8fd4'}}>{empf.vol7_min}min</div>
            <div style={{fontSize:10, color:'#444'}}>7d-Volumen</div>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:14, fontWeight:700, color:'#aaa'}}>{empf.einheiten7}</div>
            <div style={{fontSize:10, color:'#444'}}>Einheiten</div>
          </div>
          {empf.letzteGym && (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:11, fontWeight:700, color: empf.letzteGym.gruppe === 'Unterkörper' && empf.letzteGym.tage_her <= 1 ? '#c17f24' : '#666', textTransform:'uppercase'}}>
                {empf.letzteGym.gruppe}
              </div>
              <div style={{fontSize:10, color:'#444'}}>Gym vor {empf.letzteGym.tage_her}d</div>
            </div>
          )}
        </div>
      )}

      {/* Ziel-Countdown */}
      {empf?.ziele?.length > 0 && (
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          {empf.ziele.map((z, i) => (
            <div key={i} style={{background:'#141414', borderRadius:6, padding:'8px 14px', flex:1, minWidth:120}}>
              <div style={{fontSize:12, fontWeight:700, color:'#e0e0e0'}}>{z.name}</div>
              <div style={{fontSize:13, color: z.tage_bis < 14 ? '#c17f24' : '#5a8fd4', fontWeight:700, marginTop:2}}>{z.tage_bis}d</div>
            </div>
          ))}
        </div>
      )}

      {/* KI-Empfehlung */}
      <div style={{background:'#090d1f', border:'1px solid #1a2a4a', borderRadius:8, overflow:'hidden'}}>
        {!empf ? (
          <button
            onClick={laden} disabled={loading}
            style={{width:'100%', background:'transparent', border:'none', padding:'16px', color: loading ? '#4a6fa8' : '#5a8fd4', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', textAlign:'center'}}
          >
            {loading ? 'Analysiere...' : 'Empfehlung für heute laden'}
          </button>
        ) : (
          <div style={{padding:'14px 16px'}}>
            <div style={{fontSize:10, color:'#5a8fd4', letterSpacing:2, marginBottom:10}}>KI-EMPFEHLUNG</div>
            <div style={{fontSize:13, color:'#c0d0e0', lineHeight:1.8, whiteSpace:'pre-wrap'}}>
              {empf.empfehlung.replace(/\*\*/g, '')}
            </div>
            <button onClick={laden} style={{marginTop:12, background:'transparent', border:'1px solid #1a2a4a', borderRadius:4, color:'#4a6fa8', fontSize:11, padding:'4px 12px', cursor:'pointer', fontFamily:'inherit'}}>
              Neu laden
            </button>
          </div>
        )}
      </div>

      {/* HR-Zonen (dynamisch aus Einstellungen) */}
      <div style={{background:'#141414', borderRadius:8, overflow:'hidden'}}>
        <div style={{padding:'12px 16px 8px'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
            <div style={{fontSize:10, color:'#555', letterSpacing:2}}>TRAININGSZONEN (HF-BASIERT)</div>
            <button
              onClick={() => setEinstellungOpen(o => !o)}
              style={{background:'transparent', border:'1px solid #2a2a2a', borderRadius:4, color:'#444', fontSize:10, padding:'2px 8px', cursor:'pointer', fontFamily:'inherit'}}
            >
              {einstellungOpen ? 'Schließen' : 'Bearbeiten'}
            </button>
          </div>

          {/* Zone 2 — Hauptanker, prominent */}
          <div style={{background:'#0d1f0d', border:'1px solid #2a4a2a', borderRadius:6, padding:'10px 14px', marginBottom:8}}>
            <div style={{fontSize:10, color:'#4a9c6f', letterSpacing:1, marginBottom:4}}>ZONE 2 · AEROB/GRUNDLAGE</div>
            <div style={{fontSize:18, fontWeight:700, color:'#6abf7a'}}>
              {einst ? `${einst.z2_hr_low}–${einst.z2_hr_high} bpm` : empf?.zone2 ? `${empf.zone2.hr_low}–${empf.zone2.hr_high} bpm` : '—'}
            </div>
            {(empf?.zone2?.watt_ref || einst) && (
              <div style={{fontSize:11, color:'#4a7a4a', marginTop:3}}>
                {empf?.zone2?.watt_ref
                  ? `aktuell ~${empf.zone2.watt_ref}W · ${empf.zone2.watt_sessions} Sessions (60 Tage)`
                  : 'Watt-Kopplung wird aus Strava-Daten berechnet'}
              </div>
            )}
          </div>

          {/* Alle 5 Zonen */}
          <div style={{display:'flex', flexDirection:'column', gap:4}}>
            {einst?.zonen ? [
              ['Z1', einst.zonen.z1, '#555',    `< ${einst.zonen.z1.hr_high} bpm`],
              ['Z2', einst.zonen.z2, '#4a9c6f', `${einst.z2_hr_low}–${einst.z2_hr_high} bpm`],
              ['Z3', einst.zonen.z3, '#c17f24', `${einst.zonen.z3.hr_low}–${einst.zonen.z3.hr_high} bpm`],
              ['Z4', einst.zonen.z4, '#cf7f50', `${einst.zonen.z4.hr_low}–${einst.zonen.z4.hr_high} bpm`],
              ['Z5', einst.zonen.z5, '#cf4f4f', `> ${einst.zonen.z5.hr_low} bpm`],
            ].map(([key, z, col, hrStr]) => (
              <div key={key} style={{display:'grid', gridTemplateColumns:'28px 100px 1fr', gap:6, alignItems:'center'}}>
                <div style={{fontSize:10, color: col, fontWeight:700}}>{key}</div>
                <div style={{fontSize:11, color:'#333', fontStyle:'italic'}}>{z.name}</div>
                <div style={{fontSize:12, color: col, fontWeight: key === 'Z2' ? 700 : 400}}>{hrStr}</div>
              </div>
            )) : (
              <div style={{fontSize:11, color:'#333'}}>Lade Zonen...</div>
            )}
          </div>

          {/* FTP + Max HR als Referenz */}
          {empf?.ftp && (
            <div style={{marginTop:10, paddingTop:10, borderTop:'1px solid #222', display:'grid', gridTemplateColumns:'100px 1fr', gap:6}}>
              <div style={{fontSize:11, color:'#333'}}>FTP (Referenz)</div>
              <div style={{fontSize:12, color:'#555'}}>{empf.ftp}W · Max HR {einst?.max_hr || empf?.max_hr || '—'} bpm</div>
            </div>
          )}
          <div style={{marginTop:6, display:'grid', gridTemplateColumns:'100px 1fr', gap:6}}>
            <div style={{fontSize:11, color:'#333'}}>Trittfrequenz</div>
            <div style={{fontSize:12, color:'#5a8fd4'}}>85–90 rpm</div>
          </div>
        </div>

        {/* Einstellungen-Editor */}
        {einstellungOpen && (
          <div style={{borderTop:'1px solid #1a1a1a', padding:'12px 16px', display:'flex', flexDirection:'column', gap:10}}>
            <div style={{fontSize:10, color:'#555', letterSpacing:2, marginBottom:4}}>HF-EINSTELLUNGEN</div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8}}>
              <label style={{fontSize:11, color:'#555'}}>
                Max. HF
                <input
                  type="number" value={einstellungForm.max_hr}
                  onChange={e => setEinstellungForm(f => ({...f, max_hr: e.target.value}))}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
              <label style={{fontSize:11, color:'#555'}}>
                Z2 von (bpm)
                <input
                  type="number" value={einstellungForm.z2_hr_low}
                  onChange={e => setEinstellungForm(f => ({...f, z2_hr_low: e.target.value}))}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
              <label style={{fontSize:11, color:'#555'}}>
                Z2 bis (bpm)
                <input
                  type="number" value={einstellungForm.z2_hr_high}
                  onChange={e => setEinstellungForm(f => ({...f, z2_hr_high: e.target.value}))}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
            </div>
            <button
              onClick={saveEinstellungen}
              disabled={einstellungStatus === 'saving'}
              style={{
                background: einstellungStatus === 'saved' ? '#2a5c3a' : einstellungStatus === 'error' ? '#5c2a2a' : '#1a3a5a',
                color: '#fff', border:'none', borderRadius:6, padding:'10px', fontSize:13,
                fontWeight:700, cursor:'pointer', fontFamily:'inherit',
              }}
            >
              {einstellungStatus === 'saved' ? 'Gespeichert!' : einstellungStatus === 'error' ? 'Fehler' : einstellungStatus === 'saving' ? '...' : 'Speichern'}
            </button>
            <div style={{fontSize:10, color:'#333'}}>Zone 2 manuell setzen (z.B. aus Leistungsdiagnostik). Andere Zonen werden automatisch aus Max. HF berechnet.</div>
          </div>
        )}
      </div>

      {/* Manuelle Eingabe nach der Fahrt */}
      <div style={{background:'#141414', borderRadius:8, overflow:'hidden'}}>
        <button
          onClick={() => setManuell(m => !m)}
          style={{width:'100%', background:'transparent', border:'none', padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', fontFamily:'inherit'}}
        >
          <span style={{fontSize:12, color:'#888'}}>Manuelle Eingabe nach der Fahrt</span>
          <span style={{fontSize:12, color:'#555'}}>{manuell ? '▲' : '▼'}</span>
        </button>

        {manuell && (
          <div style={{padding:'0 16px 16px', display:'flex', flexDirection:'column', gap:10}}>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8}}>
              <label style={{fontSize:11, color:'#555'}}>
                Datum
                <input
                  type="date" value={form.datum}
                  onChange={e => formChange('datum', e.target.value)}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
              <label style={{fontSize:11, color:'#555'}}>
                Dauer (Min)*
                <input
                  type="number" min="1" placeholder="60" value={form.dauer_min}
                  onChange={e => formChange('dauer_min', e.target.value)}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
            </div>

            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8}}>
              <label style={{fontSize:11, color:'#555'}}>
                Ø Watt
                <input
                  type="number" placeholder="120" value={form.avg_power}
                  onChange={e => formChange('avg_power', e.target.value)}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
              <label style={{fontSize:11, color:'#555'}}>
                Ø bpm
                <input
                  type="number" placeholder="120" value={form.avg_hr}
                  onChange={e => formChange('avg_hr', e.target.value)}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                />
              </label>
              <label style={{fontSize:11, color:'#555'}}>
                RPE
                <select
                  value={form.rpe}
                  onChange={e => formChange('rpe', e.target.value)}
                  style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 4px', color: form.rpe >= 9 ? '#cf7f50' : form.rpe >= 7 ? '#c17f24' : '#e0e0e0', fontSize:12, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
                >
                  <option value="">—</option>
                  {[6,7,7.5,8,8.5,9,9.5,10].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </div>

            <label style={{fontSize:11, color:'#555'}}>
              Notiz
              <input
                type="text" placeholder="z.B. Zone 2 Grundlage, windiger Tag..." value={form.notiz}
                onChange={e => formChange('notiz', e.target.value)}
                style={{display:'block', width:'100%', marginTop:4, background:'#1a1a1a', border:'1px solid #2a2a2a', borderRadius:4, padding:'7px 8px', color:'#e0e0e0', fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box'}}
              />
            </label>

            <button
              onClick={saveManual}
              disabled={!form.dauer_min || saveStatus === 'saving'}
              style={{
                background: saveStatus === 'saved' ? '#2a5c3a' : saveStatus === 'error' ? '#5c2a2a' : form.dauer_min ? '#2a4a7c' : '#1a1a2a',
                color: form.dauer_min ? '#fff' : '#444',
                border:'none', borderRadius:6, padding:'11px', fontSize:13,
                fontWeight:700, cursor: form.dauer_min ? 'pointer' : 'default', fontFamily:'inherit',
              }}
            >
              {saveStatus === 'saved' ? 'Gespeichert!' : saveStatus === 'error' ? 'Fehler' : saveStatus === 'saving' ? '...' : 'Einheit speichern'}
            </button>
            <div style={{fontSize:10, color:'#444'}}>Strava-Sync importiert Daten automatisch — manuelle Eingabe nur ohne Strava nötig.</div>
          </div>
        )}
      </div>
    </div>
  );
}
